import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddOutlined,
  AutoAwesomeOutlined,
  ContentCopyOutlined,
  DescriptionOutlined,
  DownloadOutlined,
  HistoryOutlined,
  PrintOutlined,
  SendOutlined,
  SubdirectoryArrowRightOutlined,
} from '@mui/icons-material';
import { generateAiReport, getFacilitiyV2 } from '../../../../helpers/api';
import { userCats } from '../../../../helpers/appstate';
import { AppContext } from '../../../../store/AppContext';
import AiAccessStatus, { useAiAccess } from '../../../common/AiAccessStatus';

const BRAND = '#176b59';
const BRAND_DARK = '#125747';
const MAX_PROMPT_LENGTH = 4000;
const MAX_FOLLOW_UP_LENGTH = 1000;
const MAX_HISTORY = 10;

const outputFormats = [
  { value: 'html', label: 'Formatted report' },
  { value: 'markdown', label: 'Markdown' },
  { value: 'csv', label: 'Spreadsheet (CSV)' },
  { value: 'text', label: 'Plain text' },
  { value: 'json', label: 'JSON data' },
];

const fileExtensions = { json: 'json', markdown: 'md', html: 'html', csv: 'csv', text: 'txt' };
const mimeTypes = {
  json: 'application/json',
  markdown: 'text/markdown',
  html: 'text/html',
  csv: 'text/csv',
  text: 'text/plain',
};

const suggestedQuestions = [
  'Summarize facilities that exceeded NESREA limits last quarter, grouped by parameter.',
  'Compare compliance rates across sectors for this year.',
  'List the facilities with the most recurring exceedances and their main pollutants.',
  'Show the quarterly trend of ambient noise levels by state.',
];

const getErrorMessage = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.result?.status?.desc ||
  error?.response?.data?.status?.desc ||
  error?.message ||
  'Unable to generate this report. Please try again.';

const reportStyles = `
body{font-family:"Segoe UI",Roboto,Arial,sans-serif;color:#1f2d33;margin:0;padding:32px 40px;line-height:1.6;font-size:14px;background:#fff}
h1{font-size:22px;color:#123f35;border-bottom:2px solid ${BRAND};padding-bottom:8px;margin:0 0 16px}
h2{font-size:17px;color:${BRAND};margin:28px 0 10px}
h3{font-size:15px;color:#24433c;margin:20px 0 8px}
p{margin:0 0 12px}
ul,ol{padding-left:22px;margin:0 0 12px}
li{margin-bottom:4px}
strong,b{color:#123f35}
table{border-collapse:collapse;width:100%;margin:12px 0 20px;font-size:13px}
th{background:${BRAND};color:#fff;font-weight:600;text-align:left;padding:8px 10px;border:1px solid ${BRAND}}
td{padding:7px 10px;border:1px solid #dde5e2;vertical-align:top}
tbody tr:nth-child(even) td{background:#f6f9f8}
pre{white-space:pre-wrap;font-family:Consolas,"Courier New",monospace;font-size:13px}
@media print{
  body{padding:0}
  @page{margin:15mm}
  th{-webkit-print-color-adjust:exact;print-color-adjust:exact}
  thead{display:table-header-group}
  tr{break-inside:avoid}
  h1,h2,h3{break-after:avoid}
}`;

const makeHtmlDocument = (html, title = 'IEMS Report') => `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data:; style-src 'unsafe-inline';">
<title>${title}</title>
<style>${reportStyles}</style>
</head>
<body>${html}</body>
</html>`;

const stringifyResult = (value) => {
  if (typeof value === 'string') {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch (error) {
      return value;
    }
  }
  return JSON.stringify(value ?? '', null, 2);
};

const htmlToPlainText = (html) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.innerText || doc.body.textContent || '').trim();
};

// The report endpoint is stateless, so a follow-up re-runs the original request with the change applied
const buildFollowUpPrompt = (basePrompt, followUp) => {
  const header = 'Original report request:\n';
  const instruction = `\n\nProduce a revised version of this report, applying the following change: ${followUp}`;
  const room = Math.max(MAX_PROMPT_LENGTH - header.length - instruction.length, 0);
  return `${header}${basePrompt.slice(0, room)}${instruction}`;
};

const formatTime = (date) =>
  date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

export default function AiReport() {
  const iframeRef = useRef(null);
  const [prompt, setPrompt] = useState('');
  const [format, setFormat] = useState('html');
  const [followUp, setFollowUp] = useState('');
  const [history, setHistory] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const { access, blocked: accessBlocked, refresh: refreshAccess } = useAiAccess('assistant');

  // Consultants and personnel ask about one facility at a time (sent as facilityId).
  // EDM and regulators ask across all facilities, so they get no picker.
  const { state } = useContext(AppContext);
  const userCategory = state?.auth?.result?.data?.userCategory;
  const isFacilityUser =
    userCategory === userCats.CONSULTANT || userCategory === userCats.PERSONNEL;
  const [fallbackFacilities, setFallbackFacilities] = useState([]);
  const [facilityId, setFacilityId] = useState('');

  // The facilities and their Ask AI allowances come from /AiAccess/Me (assistant.facilities).
  // If that isn't available, fall back to the user's own facility list.
  const facilities = isFacilityUser
    ? Array.isArray(access?.facilities) && access.facilities.length
      ? access.facilities
      : fallbackFacilities
    : [];
  const canAskAbout = (f) => f && f.enabled !== false && f.remaining !== 0;
  const selectedFacility = facilities.find((f) => f.facilityId === facilityId) || null;

  const hasAccessFacilities = Boolean(access?.facilities?.length);
  useEffect(() => {
    if (!isFacilityUser || hasAccessFacilities || fallbackFacilities.length) return;
    getFacilitiyV2('', '', '', '', '', '', '', '', 1, 1000)
      .then((res) =>
        setFallbackFacilities(
          (res?.result?.data || []).map((f) => ({
            facilityId: f.facilityId,
            facilityName: f.facilityName,
            enabled: true,
            dailyLimit: null,
            remaining: null,
          }))
        )
      )
      .catch(() => setFallbackFacilities([]));
  }, [isFacilityUser, hasAccessFacilities, fallbackFacilities.length]);

  // Pick the first facility by default (the first one that can still be asked about)
  useEffect(() => {
    if (!isFacilityUser || !facilities.length) return;
    if (facilities.some((f) => f.facilityId === facilityId)) return;
    const preferred =
      facilities.find((f) => f.facilityId === access?.selectedFacilityId) ||
      facilities.find(canAskAbout) ||
      facilities[0];
    setFacilityId(preferred.facilityId);
  }, [isFacilityUser, facilities, facilityId, access?.selectedFacilityId]);

  const aiBlocked =
    accessBlocked || (isFacilityUser && (!selectedFacility || !canAskAbout(selectedFacility)));

  const activeReport = history.find((entry) => entry.id === activeId) || null;
  const reportFormat = (activeReport?.format || 'html').toLowerCase();
  const isHtml = reportFormat === 'html';
  const rawContent = activeReport?.content;
  const contentText = activeReport
    ? isHtml && typeof rawContent === 'string'
      ? rawContent
      : stringifyResult(rawContent)
    : '';

  const runReport = async ({ requestPrompt, question, parentId = null }) => {
    setError('');
    setCopied(false);
    setLoading(true);
    try {
      const result = await generateAiReport({
        prompt: requestPrompt,
        outputFormat: format,
        ...(isFacilityUser && facilityId ? { facilityId } : {}),
      });
      const entry = {
        id: Date.now(),
        parentId,
        question,
        prompt: requestPrompt,
        format: result?.outputFormat || format,
        content: result?.content ?? result?.report ?? result,
        createdAt: new Date(),
      };
      setHistory((prev) => [entry, ...prev].slice(0, MAX_HISTORY));
      setActiveId(entry.id);
      return true;
    } catch (generationError) {
      // 403 (AI off) and 429 (daily limit) carry a user-facing status.desc; neither ends the session
      setError(getErrorMessage(generationError));
      return false;
    } finally {
      setLoading(false);
      refreshAccess(); // update "requests left today"
    }
  };

  const handleGenerate = async (event) => {
    event.preventDefault();
    const question = prompt.trim();
    if (!question) return;
    await runReport({ requestPrompt: question, question });
  };

  const handleFollowUp = async (event) => {
    event.preventDefault();
    const change = followUp.trim();
    if (!change || !activeReport) return;
    const ok = await runReport({
      requestPrompt: buildFollowUpPrompt(activeReport.prompt, change),
      question: change,
      parentId: activeReport.id,
    });
    if (ok) setFollowUp('');
  };

  const handleNewReport = () => {
    setPrompt('');
    setFollowUp('');
    setActiveId(null);
    setError('');
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(isHtml ? htmlToPlainText(contentText) : contentText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (copyError) {
      setError('Clipboard access is unavailable in this browser.');
    }
  };

  const handleDownload = () => {
    const body = isHtml ? makeHtmlDocument(contentText) : contentText;
    const blob = new Blob([body], { type: `${mimeTypes[reportFormat] || 'text/plain'};charset=utf-8` });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `iems-report.${fileExtensions[reportFormat] || 'txt'}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    const frameWindow = iframeRef.current?.contentWindow;
    if (!frameWindow) return;
    frameWindow.focus();
    frameWindow.print();
  };

  const parentQuestion = activeReport?.parentId
    ? history.find((entry) => entry.id === activeReport.parentId)?.question
    : null;

  return (
    <Box sx={{ minHeight: 680, p: { xs: 2, md: 3 }, bgcolor: '#f4f6f6' }}>
      <Stack spacing={2.5}>
        {/* Page header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 44,
                height: 44,
                display: 'grid',
                placeItems: 'center',
                borderRadius: 2,
                color: '#fff',
                background: `linear-gradient(135deg, ${BRAND} 0%, #2a9479 100%)`,
                boxShadow: '0 4px 12px rgba(23,107,89,0.25)',
                flexShrink: 0,
              }}
            >
              <AutoAwesomeOutlined />
            </Box>
            <Box>
              {/* <Typography variant="h5" fontWeight={750} color="#18342f">
                ASK AI Reports
              </Typography> */}
              <Typography variant="body2" color="text.secondary">
                Ask a question about your IEMS data and get a ready-to-share report.
              </Typography>
            </Box>
          </Stack>
          {activeReport && (
            <Button
              variant="outlined"
              startIcon={<AddOutlined />}
              onClick={handleNewReport}
              disabled={loading}
              sx={{ textTransform: 'none', fontWeight: 650, color: BRAND, borderColor: '#b9d3cb' }}
            >
              New report
            </Button>
          )}
        </Stack>

        {error && (
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(300px, 360px) minmax(0, 1fr)' },
            gap: 2,
            alignItems: 'start',
          }}
        >
          {/* Left column: request + history */}
          <Stack spacing={2}>
            <Paper
              component="form"
              onSubmit={handleGenerate}
              elevation={0}
              sx={{ p: 2.5, border: '1px solid #dce4e1', borderRadius: 2 }}
            >
              <Stack spacing={2}>
                {isFacilityUser && (
                  <FormControl size="small" fullWidth>
                    <InputLabel id="ai-report-facility-label">Facility</InputLabel>
                    <Select
                      labelId="ai-report-facility-label"
                      label="Facility"
                      value={facilities.some((f) => f.facilityId === facilityId) ? facilityId : ''}
                      onChange={(event) => setFacilityId(event.target.value)}
                      disabled={loading || !facilities.length}
                      displayEmpty={!facilities.length}
                      renderValue={(value) =>
                        facilities.find((f) => f.facilityId === value)?.facilityName ||
                        'No facility assigned'
                      }
                    >
                      {facilities.map((f) => {
                        const off = f.enabled === false;
                        const usedUp = !off && f.remaining === 0;
                        return (
                          <MenuItem key={f.facilityId} value={f.facilityId} disabled={off || usedUp}>
                            <ListItemText
                              primary={f.facilityName}
                              secondary={
                                off
                                  ? 'Ask AI is off for this facility'
                                  : usedUp
                                  ? 'No questions left today'
                                  : f.dailyLimit != null
                                  ? `${f.remaining ?? f.dailyLimit} of ${f.dailyLimit} questions left today`
                                  : null
                              }
                              primaryTypographyProps={{ fontSize: 14 }}
                              secondaryTypographyProps={{ fontSize: 12 }}
                            />
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                )}

                <Typography variant="subtitle1" fontWeight={700}>
                  What would you like to know?
                </Typography>

                <TextField
                  value={prompt}
                  onChange={(event) => setPrompt(event.target.value)}
                  multiline
                  minRows={5}
                  maxRows={12}
                  fullWidth
                  placeholder="e.g. Which facilities exceeded permitted limits last quarter?"
                  inputProps={{ maxLength: MAX_PROMPT_LENGTH }}
                  helperText={prompt.length > MAX_PROMPT_LENGTH * 0.8 ? `${prompt.length}/${MAX_PROMPT_LENGTH}` : ' '}
                />

                {!prompt && (
                  <Stack spacing={0.75}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Try one of these
                    </Typography>
                    {suggestedQuestions.map((question) => (
                      <Chip
                        key={question}
                        label={question}
                        variant="outlined"
                        onClick={() => setPrompt(question)}
                        sx={{
                          height: 'auto',
                          justifyContent: 'flex-start',
                          borderColor: '#dce4e1',
                          '& .MuiChip-label': { whiteSpace: 'normal', py: 0.75, fontSize: 12.5 },
                          '&:hover': { borderColor: BRAND },
                        }}
                      />
                    ))}
                  </Stack>
                )}

                <FormControl size="small" fullWidth>
                  <InputLabel id="ai-report-format-label">Output</InputLabel>
                  <Select
                    labelId="ai-report-format-label"
                    value={format}
                    label="Output"
                    onChange={(event) => setFormat(event.target.value)}
                  >
                    {outputFormats.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disableElevation
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeOutlined />}
                  disabled={loading || aiBlocked || !prompt.trim()}
                  sx={{
                    bgcolor: BRAND,
                    textTransform: 'none',
                    fontWeight: 700,
                    py: 1.2,
                    borderRadius: 1.5,
                    '&:hover': { bgcolor: BRAND_DARK },
                  }}
                >
                  {loading ? 'Generating report...' : 'Generate report'}
                </Button>

                <AiAccessStatus
                  access={access}
                  unit="questions"
                  facility={isFacilityUser ? selectedFacility : null}
                  color={BRAND}
                />
              </Stack>
            </Paper>

            {history.length > 0 && (
              <Paper elevation={0} sx={{ border: '1px solid #dce4e1', borderRadius: 2, overflow: 'hidden' }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.25 }}>
                  <HistoryOutlined fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Typography variant="subtitle2" fontWeight={700}>
                    Recent reports
                  </Typography>
                </Stack>
                <Divider />
                <List dense disablePadding>
                  {history.map((entry) => (
                    <ListItemButton
                      key={entry.id}
                      selected={entry.id === activeId}
                      onClick={() => setActiveId(entry.id)}
                      sx={{
                        alignItems: 'flex-start',
                        '&.Mui-selected': { bgcolor: '#e8f2ef', borderLeft: `3px solid ${BRAND}` },
                      }}
                    >
                      {entry.parentId && (
                        <SubdirectoryArrowRightOutlined sx={{ fontSize: 16, mt: 0.5, mr: 0.5, color: 'text.secondary' }} />
                      )}
                      <ListItemText
                        primary={entry.question}
                        secondary={formatTime(entry.createdAt)}
                        primaryTypographyProps={{
                          fontSize: 13,
                          sx: {
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          },
                        }}
                        secondaryTypographyProps={{ fontSize: 11.5 }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            )}
          </Stack>

          {/* Right column: report document */}
          <Paper
            elevation={0}
            sx={{
              minHeight: 600,
              border: '1px solid #dce4e1',
              borderRadius: 2,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: '#fff',
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
              sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #e6ecea', bgcolor: '#fafcfb' }}
            >
              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
                <DescriptionOutlined sx={{ color: BRAND }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={700} noWrap>
                    {activeReport ? activeReport.question : 'Report'}
                  </Typography>
                  {activeReport && (
                    <Typography variant="caption" color="text.secondary" noWrap component="div">
                      {parentQuestion ? `Refinement of "${parentQuestion}" · ` : ''}
                      Generated{' '}
                      {activeReport.createdAt.toLocaleString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </Typography>
                  )}
                </Box>
              </Stack>
              {activeReport && !loading && (
                <Stack direction="row" spacing={0.5} flexShrink={0}>
                  <Tooltip title={copied ? 'Copied' : 'Copy text'}>
                    <IconButton size="small" aria-label="Copy report" onClick={handleCopy}>
                      <ContentCopyOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {isHtml && (
                    <Tooltip title="Print / Save as PDF">
                      <IconButton size="small" aria-label="Print report" onClick={handlePrint}>
                        <PrintOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Download">
                    <IconButton size="small" aria-label="Download report" onClick={handleDownload}>
                      <DownloadOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}
            </Stack>

            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              {loading ? (
                <Stack alignItems="center" justifyContent="center" spacing={1.5} sx={{ flex: 1, minHeight: 480, color: BRAND }}>
                  <CircularProgress size={32} color="inherit" />
                  <Typography variant="body2" color="text.secondary">
                    Analyzing your IEMS data...
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    This can take up to a minute for large reports.
                  </Typography>
                </Stack>
              ) : activeReport ? (
                isHtml ? (
                  <Box
                    component="iframe"
                    ref={iframeRef}
                    title="Report preview"
                    // No allow-scripts: report HTML can never execute code; same-origin + modals only enable printing
                    sandbox="allow-same-origin allow-modals"
                    srcDoc={makeHtmlDocument(contentText, activeReport.question)}
                    sx={{ display: 'block', width: '100%', flex: 1, minHeight: 520, border: 0 }}
                  />
                ) : (
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: { xs: 2, md: 3 },
                      flex: 1,
                      minHeight: 520,
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      overflowWrap: 'anywhere',
                      color: '#27353a',
                      bgcolor: '#fbfcfc',
                      fontFamily: 'Consolas, "Courier New", monospace',
                      fontSize: 13,
                      lineHeight: 1.65,
                    }}
                  >
                    {contentText}
                  </Box>
                )
              ) : (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  spacing={1.25}
                  sx={{ flex: 1, minHeight: 520, px: 3, textAlign: 'center', color: 'text.secondary' }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: '#e8f2ef',
                      mb: 0.5,
                    }}
                  >
                    <DescriptionOutlined sx={{ fontSize: 30, color: BRAND }} />
                  </Box>
                  <Typography variant="subtitle1" fontWeight={650} color="text.primary">
                    Your report will appear here
                  </Typography>
                  <Typography variant="body2" maxWidth={380}>
                    Describe what you need in plain language — a summary, a comparison, a list of facilities — and a
                    formatted report will be prepared from the data your account can access.
                  </Typography>
                </Stack>
              )}
            </Box>

            {/* Follow-up bar: refine the current report */}
            {activeReport && (
              <Box
                component="form"
                onSubmit={handleFollowUp}
                sx={{ px: 2.5, py: 1.75, borderTop: '1px solid #e6ecea', bgcolor: '#fafcfb' }}
              >
                <TextField
                  value={followUp}
                  onChange={(event) => setFollowUp(event.target.value)}
                  fullWidth
                  size="small"
                  disabled={loading || aiBlocked}
                  placeholder='Refine this report — e.g. "break it down by state" or "only include Q2"'
                  inputProps={{ maxLength: MAX_FOLLOW_UP_LENGTH }}
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: 2 } }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          type="submit"
                          edge="end"
                          aria-label="Refine report"
                          disabled={loading || aiBlocked || !followUp.trim()}
                          sx={{ color: BRAND }}
                        >
                          <SendOutlined fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            )}
          </Paper>
        </Box>
      </Stack>
    </Box>
  );
}
